import { SxProps, Theme } from "@mui/material";

export const MODAL_LANDSCAPE_COMPACT_QUERY =
  "(orientation: landscape) and (max-height: 560px)";

export const MODAL_LANDSCAPE_COMPACT_MQ =
  "@media (orientation: landscape) and (max-height: 560px)";

export const RING_SIZE = 188;
export const TABLET_RING_SIZE = 156;
export const RING_STROKE = 8;
export const RING_VISIBLE_ARC = 0.82;
export const RING_ROTATION_DEG = 125;
export const OVERVIEW_SEEK_ROW_MIN_HEIGHT_PX = 44;
// Transcript column height on tablet (`sm`–`md`); left overview stays content-sized.
export const TRANSCRIPT_MODAL_TABLET_COLUMN_VH = "48vh";

export const splitModalStyles: SxProps<Theme> = {
  display: "grid",
  gridTemplateColumns: {
    xs: "1fr",
    sm: "minmax(260px, 32vw) 1fr",
    md: "340px 1fr",
  },
  gap: { xs: 2, sm: 1.5, md: 2 },
  alignItems: { xs: "start", sm: "start", md: "stretch" },
  [MODAL_LANDSCAPE_COMPACT_MQ]: {
    gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
    columnGap: 0.5,
    rowGap: 0.5,
    alignItems: "stretch",
    "& > *": {
      minWidth: 0,
      width: "100%",
      maxWidth: "100%",
      boxSizing: "border-box",
    },
  },
};

export const transcriptLayoutStyles: SxProps<Theme> = {
  display: "grid",
  gridTemplateColumns: "86px 1fr",
  width: "100%",
  border: "1px solid",
  borderColor: "divider",
  borderRadius: "10px",
  overflow: "hidden",
};

export const transcriptRowsStyles: SxProps<Theme> = {
  maxHeight: { xs: "62vh", sm: "none" },
  overflowY: "auto",
  [MODAL_LANDSCAPE_COMPACT_MQ]: {
    maxHeight: "calc(100vh - 168px)",
  },
};

export const transcriptColumnOuterStyles: SxProps<Theme> = {
  ...transcriptLayoutStyles,
  height: {
    xs: "auto",
    sm: TRANSCRIPT_MODAL_TABLET_COLUMN_VH,
    md: "100%",
  },
  display: "flex",
  flexDirection: "column",
  minHeight: 0,
  [MODAL_LANDSCAPE_COMPACT_MQ]: {
    minWidth: 0,
    maxWidth: "100%",
    height: "calc(100vh - 120px)",
    maxHeight: "calc(100vh - 120px)",
  },
};

export const transcriptHeaderStyles: SxProps<Theme> = {
  px: 2,
  py: { xs: 1.25, sm: 1, md: 1.25 },
  borderBottom: "1px solid",
  borderColor: "divider",
  bgcolor: "background.paper",
  [MODAL_LANDSCAPE_COMPACT_MQ]: {
    px: 1.5,
    py: 0.75,
  },
};

export const transcriptHeaderTitleStyles: SxProps<Theme> = {
  fontWeight: 700,
  [MODAL_LANDSCAPE_COMPACT_MQ]: {
    fontSize: "0.95rem",
  },
};

export const transcriptScrollAreaStyles: SxProps<Theme> = {
  ...transcriptRowsStyles,
  flex: { xs: "none", sm: "1 1 0" },
  minHeight: { xs: "unset", sm: 0 },
  [MODAL_LANDSCAPE_COMPACT_MQ]: {
    flex: "1 1 0",
    minHeight: 0,
  },
};

export const markdownBodyStyles: SxProps<Theme> = {
  width: "100%",
  wordBreak: "break-word",
  "& ul": { margin: 0, marginBottom: "1em" },
  pt: 0.5,
};

export const overviewCardStyles = (theme: Theme): SxProps<Theme> => {
  const isDark = theme.palette.mode === "dark";
  // Top inset matches `transcriptHeaderStyles` py so "Audio Overview" lines up with "Interactive Transcript".
  const sideAndBottom = { xs: 2.25, sm: 1.5, md: 2.25 } as const;
  const topWithHeader = { xs: 1.25, sm: 1, md: 1.25 } as const;
  return {
    border: "1px solid",
    borderColor: isDark ? "#44566f" : "#dce6fb",
    borderRadius: "18px",
    pt: topWithHeader,
    px: sideAndBottom,
    pb: sideAndBottom,
    bgcolor: isDark ? "#2f3b4d" : "#f5f9ff",
    height: "auto",
    maxHeight: {
      xs: "none",
      sm: TRANSCRIPT_MODAL_TABLET_COLUMN_VH,
      md: "none",
    },
    boxSizing: "border-box",
    overflowY: { xs: "visible", sm: "auto", md: "visible" },
    minHeight: { sm: 0, md: "unset" },
    [MODAL_LANDSCAPE_COMPACT_MQ]: {
      width: "100%",
      maxWidth: "100%",
      minWidth: 0,
      pt: 1,
      px: 1.25,
      pb: 1,
      borderRadius: "14px",
      maxHeight: "calc(100vh - 120px)",
      overflowY: "auto",
    },
  };
};

export const overviewHeaderRowStyles: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 1,
  mb: { xs: 1.25, sm: 0.75, md: 1.25 },
  [MODAL_LANDSCAPE_COMPACT_MQ]: {
    mb: 0.5,
    gap: 0.75,
  },
};

// Left column in landscape: avatar + name side-by-side with ring
export const overviewLandscapePlayerRowStyles: SxProps<Theme> = {
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "center",
  gap: 1.75,
  width: "100%",
  mb: 0.5,
  flexWrap: "nowrap",
};

export const overviewLandscapeIdentityColumnStyles: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  flex: "0 0 auto",
  minWidth: 0,
  maxWidth: 92,
};

export const overviewLandscapeTransportColumnStyles: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  flex: "0 0 auto",
  justifyContent: "center",
};

export const overviewSectionTitleStyles = (theme: Theme): SxProps<Theme> => {
  const isDark = theme.palette.mode === "dark";
  return {
    fontWeight: 700,
    mb: 0,
    textAlign: "left",
    flex: "1 1 auto",
    minWidth: 0,
    color: isDark ? "rgba(245, 250, 255, 0.96)" : "rgba(21, 34, 54, 0.96)",
    [MODAL_LANDSCAPE_COMPACT_MQ]: {
      fontSize: "0.95rem",
    },
  };
};

export const overviewSpeedFormStyles: SxProps<Theme> = {
  minWidth: { xs: 64, sm: 60 },
  flexShrink: 0,
};

export const overviewSpeedSelectStyles = (theme: Theme): SxProps<Theme> => {
  const isDark = theme.palette.mode === "dark";
  const accent = isDark ? "#5ee7f7" : "#06B6D4";
  return {
    width: "64px",
    fontSize: "0.75rem",
    borderRadius: "12px",
    minHeight: 32,
    height: 32,
    boxSizing: "border-box",
    [MODAL_LANDSCAPE_COMPACT_MQ]: {
      width: "56px",
      minHeight: 28,
      height: 28,
      fontSize: "0.7rem",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: accent,
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: accent,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: accent,
    },
    "& .MuiSvgIcon-root": {
      color: accent,
    },
    color: accent,
  };
};

export const overviewAvatarStyles = (theme: Theme): SxProps<Theme> => {
  const isDark = theme.palette.mode === "dark";
  return {
    width: { xs: 72, sm: 56, md: 72 },
    height: { xs: 72, sm: 56, md: 72 },
    borderRadius: "50%",
    objectFit: "cover",
    mb: { xs: 1, sm: 0.5, md: 1 },
    mx: "auto",
    boxShadow: isDark
      ? "0 0 0 1px rgba(255,255,255,0.08)"
      : "0 0 0 1px rgba(0,0,0,0.04)",
    [MODAL_LANDSCAPE_COMPACT_MQ]: {
      width: 44,
      height: 44,
      mb: 0.35,
    },
  };
};

export const overviewDoctorNameStyles = (theme: Theme): SxProps<Theme> => {
  const isDark = theme.palette.mode === "dark";
  return {
    mb: { xs: 1.5, sm: 0.75, md: 1.5 },
    textAlign: "center",
    color: isDark ? "rgba(245, 250, 255, 0.96)" : "rgba(21, 34, 54, 0.96)",
    fontWeight: 600,
    [MODAL_LANDSCAPE_COMPACT_MQ]: {
      mb: 0,
      fontSize: "0.8125rem",
      lineHeight: 1.2,
    },
  };
};

export const overviewRingContainerStyles: SxProps<Theme> = {
  width: { xs: RING_SIZE, sm: TABLET_RING_SIZE, md: RING_SIZE },
  height: { xs: RING_SIZE, sm: TABLET_RING_SIZE, md: RING_SIZE },
  position: "relative",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  mx: "auto",
  mt: { xs: 0.25, sm: 0, md: 0.25 },
  [MODAL_LANDSCAPE_COMPACT_MQ]: {
    width: 118,
    height: 118,
    mt: 0,
    mx: 0,
  },
};

export const overviewProgressSvgStyles = (
  ringRotationDeg: number,
): SxProps<Theme> => ({
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  pointerEvents: "none",
  transform: `rotate(${ringRotationDeg}deg)`,
});

export const overviewPlayButtonStyles = (
  hasHandler: boolean,
): SxProps<Theme> => ({
  width: { xs: 68, sm: 56, md: 68 },
  height: { xs: 68, sm: 56, md: 68 },
  borderRadius: "50%",
  border: "none",
  background: "transparent",
  p: 0,
  m: 0,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  opacity: hasHandler ? 1 : 0.6,
  position: "relative",
  zIndex: 2,
  [MODAL_LANDSCAPE_COMPACT_MQ]: {
    width: 42,
    height: 42,
  },
});

export const overviewTimeRowStyles: SxProps<Theme> = {
  display: "flex",
  justifyContent: "center",
  mt: { xs: -1.25, sm: -0.75, md: -1.25 },
  mb: { xs: 1.25, sm: 0.75, md: 1.25 },
  [MODAL_LANDSCAPE_COMPACT_MQ]: {
    mt: -0.35,
    mb: 0,
  },
};

// Time row stacked directly under ring in horizontal landscape layout
export const overviewTimeRowLandscapeInlineStyles: SxProps<Theme> = {
  display: "flex",
  justifyContent: "center",
  mt: 0,
  mb: 0,
};

export const overviewTimeTextStyles = (theme: Theme): SxProps<Theme> => {
  const isDark = theme.palette.mode === "dark";
  return {
    color: isDark ? "rgba(220, 231, 247, 0.82)" : "text.secondary",
    fontSize: { xs: 16, sm: 14, md: 16 },
    letterSpacing: 0.2,
    [MODAL_LANDSCAPE_COMPACT_MQ]: {
      fontSize: "0.75rem",
    },
  };
};

export const overviewLessonTitleStyles = (theme: Theme): SxProps<Theme> => {
  const isDark = theme.palette.mode === "dark";
  return {
    fontWeight: 600,
    mb: { xs: 1, sm: 0.5, md: 1 },
    textAlign: "center",
    mt: { xs: 0.25, sm: 0, md: 0.25 },
    fontSize: { xs: "1.08rem", sm: "0.95rem", md: "1.08rem" },
    color: isDark ? "rgba(245, 250, 255, 0.96)" : "rgba(21, 34, 54, 0.96)",
    [MODAL_LANDSCAPE_COMPACT_MQ]: {
      fontSize: "0.72rem",
      mb: 0.35,
      mt: 0,
      lineHeight: 1.3,
      display: "-webkit-box",
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical",
      overflow: "hidden",
      textAlign: "center",
    },
  };
};

export const overviewSeekRowStyles: SxProps<Theme> = {
  width: "100%",
  minHeight: OVERVIEW_SEEK_ROW_MIN_HEIGHT_PX,
  pt: 0.5,
  pb: 0.25,
  display: "flex",
  alignItems: "center",
  [MODAL_LANDSCAPE_COMPACT_MQ]: {
    minHeight: 30,
    pt: 0.15,
    pb: 0,
  },
};

export const ringTrackColor = (theme: Theme): string =>
  theme.palette.mode === "dark"
    ? "rgba(40, 201, 220, 0.18)"
    : "rgba(40, 201, 220, 0.2)";

export const transcriptRowButtonStyles = (
  hasSeek: boolean,
): SxProps<Theme> => ({
  width: "100%",
  display: "grid",
  gridTemplateColumns: "86px 1fr",
  alignItems: "stretch",
  textAlign: "left",
  cursor: hasSeek ? "pointer" : "default",
  border: 0,
  p: 0,
  bgcolor: "transparent",
  transition: "background-color .15s ease",
  "&:hover": hasSeek ? { bgcolor: "action.hover" } : undefined,
  [MODAL_LANDSCAPE_COMPACT_MQ]: {
    gridTemplateColumns: "70px 1fr",
  },
});

export const transcriptTimeCellStyles = (
  isActive: boolean,
): SxProps<Theme> => ({
  px: 1.25,
  py: 1.5,
  fontFamily: "monospace",
  fontSize: "12px",
  color: isActive ? "primary.main" : "text.secondary",
  bgcolor: isActive ? "action.selected" : "action.hover",
  borderRight: isActive ? "2px solid" : "1px solid",
  borderBottom: "1px solid",
  borderRightColor: isActive ? "primary.main" : "divider",
  borderBottomColor: "divider",
  [MODAL_LANDSCAPE_COMPACT_MQ]: {
    px: 0.75,
    py: 1,
    fontSize: "11px",
  },
});

export const transcriptTextCellStyles = (
  isActive: boolean,
): SxProps<Theme> => ({
  px: 2,
  py: 1.5,
  bgcolor: isActive ? "rgba(255, 235, 59, 0.18)" : "transparent",
  borderBottom: "1px solid",
  borderColor: "divider",
  [MODAL_LANDSCAPE_COMPACT_MQ]: {
    px: 1.25,
    py: 1,
  },
});

export const transcriptBodyTypographyStyles = (
  isActive: boolean,
): SxProps<Theme> => ({
  color: isActive ? "primary.main" : "text.primary",
  fontWeight: isActive ? 700 : 400,
  lineHeight: 1.45,
  [MODAL_LANDSCAPE_COMPACT_MQ]: {
    fontSize: "0.8125rem",
    lineHeight: 1.4,
  },
});

// word / sentence “karaoke” inside an active segment
export const transcriptTokenStyles = (
  theme: Theme,
  phase: "past" | "current" | "future",
): SxProps<Theme> => {
  const isDark = theme.palette.mode === "dark";
  const muted = isDark ? "rgba(255,255,255,0.42)" : "rgba(0,0,0,0.45)";
  return {
    transition: "color 0.14s ease, opacity 0.14s ease, font-weight 0.14s ease",
    ...(phase === "future" && {
      color: muted,
      fontWeight: 400,
      opacity: 1,
    }),
    ...(phase === "past" && {
      color: theme.palette.primary.main,
      fontWeight: 500,
      opacity: 0.82,
    }),
    ...(phase === "current" && {
      color: theme.palette.primary.main,
      fontWeight: 700,
      opacity: 1,
    }),
  };
};

export const dialogActionsStyles: SxProps<Theme> = {
  px: 2,
  pb: 2,
  [MODAL_LANDSCAPE_COMPACT_MQ]: {
    px: 1.5,
    py: 0.5,
    pb: 1,
  },
};

export const closeButtonStyles = (theme: Theme): SxProps<Theme> => ({
  ...(theme.palette.mode === "dark" && {
    color: theme.palette.common.white,
  }),
});
